'use client'

import { categoryByKey, MIN_PICKS } from '../_lib/data'
import type { CategoryKey } from '../types'
import { OrderedTile } from './OrderedTile'

export function RankStep({
  pool,
  ranked,
  onRank,
  onUnrank,
  onNudge,
  onNext,
}: {
  pool: CategoryKey[]
  ranked: CategoryKey[]
  onRank: (k: CategoryKey) => void
  onUnrank: (k: CategoryKey) => void
  onNudge: (k: CategoryKey, direction: -1 | 1) => void
  onNext: () => void
}) {
  const blocked = ranked.length < MIN_PICKS

  return (
    <div>
      <h1 className="text-title-md text-gray-darker">
        Sort what your money is <em className="text-green-darker">for</em>
      </h1>
      <p className="mt-3 max-w-150 text-body text-neutral-darker">
        Select the pillars that matter to you, then sort from the most important to the least important in descending
        order. You can choose to include all or leave some behind.
      </p>

      <div className="mt-6 grid gap-4 tablet:grid-cols-2 tablet:items-start">
        <section>
          <h2 className="mb-2 flex items-baseline justify-between text-body-xs font-bold text-neutral-darker uppercase">
            All ten
            <span className="tabular-nums">{pool.length ? `${pool.length} left` : ''}</span>
          </h2>
          <ul className="flex min-h-45 flex-col gap-2 rounded-2xl bg-bone-warm p-2.5">
            {pool.map((k) => {
              const category = categoryByKey(k)
              return (
                <li key={k} className="rounded-xl bg-card p-3 shadow-sm">
                  <div className="flex items-start gap-2.5">
                    <div className="flex-1">
                      <p className="text-body-sm-bold text-gray-darker">{category.n}</p>
                      <p className="text-body-xs text-neutral-darker">{category.d}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRank(k)}
                      aria-label={`Add ${category.n} to your list`}
                      className="flex size-7 shrink-0 items-center justify-center rounded-full bg-bone-warm text-neutral-darker hover:bg-green-lighter-2 hover:text-black"
                    >
                      <span aria-hidden="true">+</span>
                    </button>
                  </div>
                </li>
              )
            })}
            {pool.length === 0 && (
              <li className="p-4 text-center text-body-sm text-neutral-darker">You have ranked all ten.</li>
            )}
          </ul>
        </section>

        <section>
          <h2 className="mb-2 flex items-baseline justify-between text-body-xs font-bold text-neutral-darker uppercase">
            What matters, in order
            <span className="tabular-nums">{ranked.length ? `${ranked.length} chosen` : ''}</span>
          </h2>
          <ul className="flex min-h-45 flex-col gap-2 rounded-2xl bg-bone-warm p-2.5">
            {ranked.map((k, i) => {
              const category = categoryByKey(k)
              return (
                <OrderedTile
                  key={k}
                  rank={i + 1}
                  title={category.n}
                  subtitle={category.d}
                  isFirst={i === 0}
                  isLast={i === ranked.length - 1}
                  onMoveUp={() => onNudge(k, -1)}
                  onMoveDown={() => onNudge(k, 1)}
                  onRemove={() => onUnrank(k)}
                  removeLabel={`Take ${category.n} out of your list`}
                />
              )
            })}
            {ranked.length === 0 && (
              <li className="p-4 text-center text-body-sm text-neutral-darker">
                Tap + to add values. The most important ones go at the top.
              </li>
            )}
          </ul>
        </section>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          onClick={onNext}
          disabled={blocked}
          className="rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-45"
        >
          Next
        </button>
        {blocked && <p className="text-body-sm text-neutral-darker">Add at least {MIN_PICKS} to continue.</p>}
      </div>
    </div>
  )
}
